/* eslint-disable import/prefer-default-export */
import { getLongFromShortCode } from '@homeaway/google-address-parser';
import * as pages from './PageList';
import * as predicates from './Predicates';
import { getHostDetails, getLocale } from '../../utils/LocaleMap';
import { getAbovCookie } from '../../utils/CookieUtil';
import { fetchAuthUrlWithService } from '../../utils/AuthUrlUtil';
import reasons from '../ListYourPropertyPage/EligibilityCatch/reasons';
import { findByLocale } from '../../utils/PosUtil';
import { collapseQueryParams } from '../../utils/StringUtil';
import {
    ARX_ADDRESS_URL, EPC_UPL_REDIRECT_URLS, isVrBaseLocale, pathUrls
} from '../../constants/common';
import {
    ENCRYPTION_QUERY, ENGLISH_ADDRESS_QUERY, GET_NEXT_PAGE_QUERY, retrieveData
} from '../../actions/graphQlSvc';
import log from '../../utils/ClientLogger';
import { getFuzzyLatLng } from '../../utils/LocationUtil';

export const ASYNC_FUNC = 'ASYNC_FUNC';
const hostMappingForUFO = {
    'www.stayz.com.au': 'www.vrbo.com',
    'www.bookabach.co.nz': 'www.vrbo.com'
};

const isTest = () => /integration|local|test|stage/.test(window.location?.href);

const postLocationConfigs = [
    {
        predicate: [
            predicates.isVrbo,
            predicates.isUserLoggedIn,
            predicates.isOwner,
            predicates.isMissingAdvertiser
        ],
        pageName: pages.UPDATE_ACCOUNT_PAGE
    },
    {
        predicate: [
            predicates.isVrbo,
            predicates.isUserLoggedIn,
            predicates.isTraveler,
            predicates.isNotMissingDetails
        ],
        pageName: pages.UPDATE_ACCOUNT_PAGE
    },
    {
        predicate: [
            predicates.isVrbo,
            predicates.isUserLoggedIn,
            predicates.isMissingDetails
        ],
        pageName: pages.MISSING_DETAILS_PAGE
    },
    {
        predicate: [
            predicates.isEstimatePageRemoved,
            predicates.isVrbo,
            predicates.isUserLoggedIn,
            predicates.isOwner
        ],
        pageName: pages.VR_PROPERTY_CREATION
    },
    {
        predicate: [
            predicates.isVrbo,
            predicates.isUserLoggedIn,
            predicates.isOwner
        ],
        pageName: pages.ESTIMATE_PAGE
    },
    {
        predicate: [
            predicates.isEg,
            predicates.isExternalFlow,
            predicates.isAAPV2
        ],
        pageName: pages.CL_PROPERTY_INFO
    },
    {
        predicate: [
            predicates.isEg,
            predicates.isExternalFlow
        ],
        pageName: pages.EXTERNAL_FLOW_PAGE
    },
    {
        predicate: [
            predicates.isEg
        ],
        pageName: pages.CL_PROPERTY_INFO
    },
    {
        predicate: [
            predicates.isVrboUplEnabled
        ],
        pageName: pages.VRBO_UPL_PAGE
    },
    {
        pageName: pages.LEAD_PAGE
    }
];

// predicates is an array of arrays
const generateConfig = (predicateGroups, pageName) => predicateGroups.map((predicateGroup) => ({
    predicate: predicateGroup,
    pageName
}));

const predicatesForManualLocation = [
    [predicates.hasNoLatLng],
    [predicates.isAddressInvalid],
    [predicates.hasUnitNumberError],
    [predicates.isInvalidByGeoCoder]
];

const locationToManualLocationConfig = generateConfig(predicatesForManualLocation, pages.MANUAL_LOCATION_PAGE);

const aapLocationToManualLocationConfig = generateConfig(predicatesForManualLocation, pages.AAPMANUAL_LOCATION_PAGE);

const pageStates = [
    {
        name: pages.BED_BATH_PAGE,
        link: ({ features, query, locale }) => {
            if (typeof window === 'object' && features.isConventionalLodgingFlow) {
                // eslint-disable-next-line max-len
                const vrboLocale = getLocale(new URLSearchParams(window.location.search))
                    || getLocale(new URLSearchParams(query))
                    || findByLocale(locale.display).localeVR;
                const destinationUrl = `${window.location.protocol}//${getHostDetails(vrboLocale)}/${vrboLocale}/list?theme=mvp&utm_medium=referral&utm_source=ULX_MVP`;
                return destinationUrl;
            }
            return 'property-details';
        },
        nextPages: [
            {
                predicate: [
                    predicates.isUserLocationInGoogleMapsExclusionList
                ],
                pageName: pages.MANUAL_LOCATION_PAGE
            },
            {
                pageName: pages.LOCATION_PAGE
            }
        ]
    },
    {
        name: pages.UPDATE_ACCOUNT_PAGE,
        link: 'update-account',
        nextPages: [
            {
                predicate: [
                    predicates.isEstimatePageRemoved,
                    predicates.isNotCAS
                ],
                pageName: pages.VR_PROPERTY_CREATION
            },
            {
                predicate: [
                    predicates.isNotCAS
                ],
                pageName: pages.ESTIMATE_PAGE
            },
            {
                pageName: pages.VALIDATE_AUTH_TICKET
            }
        ]
    },
    {
        name: pages.UPGRADE_AUTH_REDIRECT,
        link: ({ autoAuthUrl }) => autoAuthUrl
    },
    {
        name: pages.LOCATION_PAGE,
        link: 'location',
        nextPages: [
            // Shared space deflection: Vrbo partners always confirm on the manual page
            {
                predicate: [
                    predicates.isVrbo,
                    predicates.isSharedSpaceDeflection
                ],
                pageName: pages.MANUAL_LOCATION_PAGE
            },
            ...locationToManualLocationConfig,
            {
                predicate: [
                    predicates.isAddressV2Feature
                ],
                pageName: pages.CONFIRM_LOCATION
            },
            ...postLocationConfigs
        ]
    },
    {
        name: pages.MANUAL_LOCATION_PAGE,
        link: 'manual-location',
        nextPages: [
            {
                predicate: [
                    predicates.isAddressV2Feature
                ],
                pageName: pages.CONFIRM_LOCATION
            },
            ...postLocationConfigs
        ]
    },
    {
        name: pages.CONFIRM_LOCATION,
        link: 'confirm-location',
        nextPages: [
            ...postLocationConfigs
        ]
    },
    {
        name: pages.AAPLOCATION_PAGE,
        link: 'add-property/location',
        nextPages: [
            ...aapLocationToManualLocationConfig,
            {
                predicate: [
                    predicates.isAddressV2Feature
                ],
                pageName: pages.AAPCONFIRM_LOCATION
            },
            {
                pageName: pages.AAPCL_PROPERTY_INFO
            }
        ]
    },
    {
        name: pages.AAPMANUAL_LOCATION_PAGE,
        link: 'add-property/manual-location',
        nextPages: [
            {
                predicate: [
                    predicates.isAddressV2Feature
                ],
                pageName: pages.AAPCONFIRM_LOCATION
            },
            {
                pageName: pages.AAPCL_PROPERTY_INFO
            }
        ]
    },
    {
        name: pages.AAPCONFIRM_LOCATION,
        link: 'add-property/confirm-location',
        nextPages: [
            {
                pageName: pages.AAPCL_PROPERTY_INFO
            }
        ]
    },
    {
        name: pages.AAPCL_PROPERTY_INFO,
        link: 'add-property/property-info',
        // no next pages so far, when the join is submitted the handler
        // config will kick in
        nextPages: [
            {
                predicate: [
                    predicates.isEg,
                    predicates.isClRejected
                ],
                pageName: pages.CL_ELIGIBILITY_CHECK
            },
            {
                predicate: [
                    predicates.isEg
                ],
                pageName: pages.CL_CONTRACT_ONBOARDING_AAP
            }
        ]
    },
    {
        name: pages.VALIDATE_AUTH_TICKET,
        link: ({ locale, autoAuthUrl, isEstimatePageRemoved }) => {
            if (typeof window === 'object') {
                // eslint-disable-next-line max-len
                const estimatePageLink = pageStates.find((x) => x.name === pages.ESTIMATE_PAGE).url;
                const propertyCreationPageLink = pageStates.find((x) => x.name === pages.VR_PROPERTY_CREATION).url;
                const linkToUse = isEstimatePageRemoved ? propertyCreationPageLink : estimatePageLink;
                const abovCookie = getAbovCookie();
                const abov = abovCookie ? `?abov=${abovCookie}` : '';

                const { pathname } = window.location;
                const segments = pathname.split('/').filter(Boolean);
                // Derive the current app path (the segment after the locale) so we preserve
                // custom app paths like `list-one-key` instead of hardcoding `list`.
                const appPath = segments.length > 1 ? segments[1] : 'list';

                const destinationUrl = `${window.location.protocol}//${window.location.host}/${locale.display}/${appPath}/${linkToUse}${abov}`;
                const fullAuthUrl = fetchAuthUrlWithService(autoAuthUrl, destinationUrl);
                return fullAuthUrl;
            }
            // This should never be called server side, but we have to return something.
            return '';
        }
    },
    {
        name: pages.CL_PROPERTY_INFO,
        link: 'property-info',
        nextPages: [
            {
                predicate: [
                    predicates.isSelfServe
                ],
                pageName: pages.EPC_UPL_PAGE
            },
            {
                pageName: pages.LEAD_PAGE
            }
        ]
    },
    {
        name: pages.CL_CONTRACT_ONBOARDING,
        link: ({ propertyId }) => {
            // eslint-disable-next-line eqeqeq
            if (!propertyId || propertyId == 0) {
                return 'join-creation/confirm';
            }
            const baseUrl = isTest()
                ? `${window.location.protocol}//apps.expediapartnercentral.com.integration.sb.karmalab.net`
                : `${window.location.protocol}//apps.expediapartnercentral.com`;
            const htidParam = propertyId ? `?htid=${propertyId}` : '';
            return `${baseUrl}/onboarding/contractSign${htidParam}`;
        }
    },
    {
        name: pages.CL_CONTRACT_ONBOARDING_AGENTIC,
        link: ({ propertyId }) => {
            const baseUrl = isTest()
                ? `${window.location.protocol}//apps.expediapartnercentral.com.integration.sb.karmalab.net`
                : `${window.location.protocol}//apps.expediapartnercentral.com`;
            const htidParam = propertyId ? `?htid=${propertyId}` : '';
            return `${baseUrl}/onboarding/agentic/contractSign${htidParam}`;
        }
    },
    {
        name: pages.CL_ELIGIBILITY_CHECK,
        link: ({ eligibilityStatus }) => {
            switch (eligibilityStatus) {
            case reasons.DEFAULT:
            case reasons.LOCATION:
                return 'eligibility/location';
            case reasons.SIZE_LOCATION:
                return 'eligibility/size';
            case reasons.VRBO:
                return 'eligibility/vrbo';
            case reasons.ADD_PROPERTY:
                return 'eligibility/add-property';
            case reasons.TIMEOUT:
                return 'add-property/join/timeout';
            case reasons.AAP_INELIGIBLE:
                return 'add-property/ineligible';
            default:
                return 'eligibility/location';
            }
        },
        nextPages: [
            {
                predicate: [
                    predicates.isAAPRedirect
                ],
                pageName: pages.AAPLOCATION_PAGE
            },
            {
                predicate: [
                    predicates.isEg,
                    predicates.isTimeout
                ],
                pageName: pages.MULTIPROPERTY_ONBOARDING
            },
            {
                predicate: [
                    predicates.isRedirectVrbo
                ],
                pageName: pages.BED_BATH_PAGE
            }
        ]
    },
    {
        name: pages.MULTIPROPERTY_ONBOARDING,
        link: () => {
            const baseUrl = isTest()
                ? `${window.location.protocol}//apps.expediapartnercentral.com.integration.sb.karmalab.net`
                : `${window.location.protocol}//apps.expediapartnercentral.com`;
            return `${baseUrl}/lodging/multiproperty/MultiProperty.html`;
        }
    },
    {
        name: pages.LEAD_PAGE,
        link: 'lead',
        nextPages: [
            {
                predicate: [
                    predicates.isEg,
                    predicates.isClRejected
                ],
                pageName: pages.CL_ELIGIBILITY_CHECK
            },
            {
                predicate: [
                    predicates.isEg,
                    predicates.isNotSelfServe
                ],
                pageName: pages.CL_ELIGIBILITY_CHECK
            },
            {
                predicate: [
                    predicates.isEstimatePageRemoved
                ],
                pageName: pages.VR_PROPERTY_CREATION
            },
            {
                pageName: pages.ESTIMATE_PAGE
            }
        ]
    },
    {
        name: pages.LOGIN_INTERSTITIAL,
        link: 'login-interstitial',
        nextPages: [
            {
                predicate: [
                    predicates.isUserLoggedIn,
                    predicates.isOwner,
                    predicates.isMissingAdvertiser
                ],
                pageName: pages.UPDATE_ACCOUNT_PAGE
            },
            {
                predicate: [
                    predicates.isUserLoggedIn,
                    predicates.isTraveler,
                    predicates.isNotMissingDetails
                ],
                pageName: pages.UPDATE_ACCOUNT_PAGE
            },
            {
                predicate: [
                    predicates.isUserLoggedIn,
                    predicates.isMissingDetails
                ],
                pageName: pages.MISSING_DETAILS_PAGE
            },
            {
                predicate: [
                    predicates.isEstimatePageRemoved,
                    predicates.isUserLoggedIn,
                    predicates.isOwner
                ],
                pageName: pages.VR_PROPERTY_CREATION
            },
            {
                predicate: [
                    predicates.isUserLoggedIn,
                    predicates.isOwner
                ],
                pageName: pages.ESTIMATE_PAGE
            },
            {
                pageName: pages.BED_BATH_PAGE
            }
        ]
    },
    {
        name: pages.MISSING_DETAILS_PAGE,
        link: 'missing-details',
        nextPages: [
            {
                pageName: pages.VALIDATE_AUTH_TICKET
            }
        ]
    },
    {
        name: pages.VR_PROPERTY_CREATION,
        url: 'property-creation',
        link: 'property-creation',
        nextPages: [
            {
                pageName: pages.UNIFIED_ONBOARDING
            }
        ]
    },
    {
        name: pages.ESTIMATE_PAGE,
        url: 'estimate-results',
        link: 'estimate-results',
        nextPages: [
            {
                pageName: pages.UNIFIED_ONBOARDING
            }
        ]
    },
    {
        name: pages.UNIFIED_ONBOARDING,
        link: ({ expediaId, locale }) => {
            const hostDetails = window.location.host;
            const hostToUse = hostMappingForUFO[hostDetails] || hostDetails;
            return `${window.location.protocol}//${hostToUse}/${locale.display}/supply/onboard/welcome?propertyId=${expediaId}`;
        }
    },
    {
        name: pages.CLASSIFICATION,
        link: 'classification',
        nextPages: [
            {
                predicate: [
                    predicates.isLodgingFlow,
                    predicates.isArx
                ],
                pageName: pages.ARX_REDIRECT
            },
            {
                predicate: [
                    predicates.isLodgingFlow,
                    predicates.isUserLocationInGoogleMapsExclusionList
                ],
                pageName: pages.MANUAL_LOCATION_PAGE
            },
            {
                predicate: [
                    predicates.isLodgingFlow
                ],
                pageName: pages.LOCATION_PAGE
            },
            {
                pageName: pages.BED_BATH_PAGE
            }
        ]
    },
    {
        name: pages.LANGUAGE_SELECTION,
        link: ({ locale }) => {
            // eslint-disable-next-line max-len
            const vrboLocale = getLocale(new URLSearchParams(window.location.search), locale.canonical);
            const destinationUrl = `${window.location.protocol}//${getHostDetails(vrboLocale)}/${vrboLocale}/list/languageSelection?theme=mvp&utm_medium=referral&utm_source=ULX_MVP`;
            return destinationUrl;
        },
        nextPages: [
            {
                pageName: pages.BED_BATH_PAGE
            }
        ]
    },
    {
        name: pages.EXTERNAL_FLOW_PAGE,
        link: ASYNC_FUNC,
        asyncLink: async ({
            address,
            latLng,
            pathSource,
            addressValidation,
            placeId,
            langId,
            serverAddressValidation,
            isAddressV2,
            geocoderLatLng,
            partialMatch
        }) => {
            const isUnicode = !Object.keys(addressValidation).reduce(
                (acc, x) => acc && addressValidation[x].isLatin,
                true
            );
            const addressToUse = isUnicode
                ? (await retrieveData(ENGLISH_ADDRESS_QUERY, { placeId })).data.fetchEnglishAddress
                : address;
            const {
                fuzzy,
                latitude,
                longitude
            } = getFuzzyLatLng(
                isAddressV2,
                serverAddressValidation,
                latLng,
                geocoderLatLng,
                partialMatch
            );
            const latLngToUse = {
                lat: latitude,
                lng: longitude
            };
            let { country } = addressToUse;
            if (country.length === 2) country = getLongFromShortCode(country);
            const value = JSON.stringify({
                address: {
                    address1: addressToUse.address1,
                    address2: addressToUse.address2,
                    city: addressToUse.city,
                    stateProvince: addressToUse.stateProvince,
                    postalCode: addressToUse.postalCode,
                    country,
                    ...latLngToUse,
                    fuzzy
                },
                localizedAddress: isUnicode ? {
                    ...address,
                    country,
                    ...latLngToUse,
                    fuzzy,
                    languageCode: langId
                } : null
            });
            log('info', pages.EXTERNAL_FLOW_PAGE, {
                ...JSON.parse(value),
                pathSource,
                isAddressV2,
                partialMatch,
                userAction: 'Property completion'
            });
            const result = (await retrieveData(ENCRYPTION_QUERY, { value })).data.encryptValue;
            return `${isTest() ? pathUrls[pathSource].test : pathUrls[pathSource].prod}${collapseQueryParams({ data: result })}`;
        }
    },
    {
        name: pages.CL_CONTRACT_ONBOARDING_AAP,
        link: ASYNC_FUNC,
        asyncLink: async ({
            propertyId, isPaperContractUser
        }) => {
            const returnUrl = 'add-property/join/timeout';
            // eslint-disable-next-line eqeqeq
            if (!propertyId || propertyId == 0) {
                return returnUrl;
            }
            const result = (await retrieveData(GET_NEXT_PAGE_QUERY, {
                propertyId,
                additionalData: {
                    currentStep: 'basicPropertyHotelier',
                    paperOnlyContracts: isPaperContractUser ? 'true' : 'false'
                }
            })).data.getNext;
            if (result) {
                return `${window.location.protocol}//${window.location.host}${result}`;
            }
            return returnUrl;
        }
    },
    {
        name: pages.EPC_UPL_PAGE,
        link: ({ locale }) => {
            const destinationUrl = `${window.location.protocol}//${window.location.host}/${locale.display}/list/join-creation`;
            return EPC_UPL_REDIRECT_URLS(isTest(), locale.display, destinationUrl);
        }
    },
    {
        name: pages.ARX_REDIRECT,
        link: ({ locale }) => ARX_ADDRESS_URL(`${window.location.protocol}//${window.location.host}`, locale?.display)
    },
    {
        name: pages.VRBO_UPL_PAGE,
        link: ({ locale }) => {
            if (typeof window === 'object') {
                const abovCookie = getAbovCookie();
                const abov = abovCookie ? `?abov=${encodeURIComponent(abovCookie)}` : '';
                const loginInterstitialLink = `/${locale.display}/list/login-interstitial${abov}`;
                const host = window.location.host.includes('local') ? 'stage.vrbo.com' : window.location.host;
                const uplLocale = isVrBaseLocale(host, locale.display) ? '' : `${locale.display}/`;
                const uplUrl = new URL(`https://${host}/${uplLocale}v1/identity/sso/vrbo/authorize`);
                uplUrl.searchParams.set('idp', '0oa3j4khoponmbuxp1d9');
                uplUrl.searchParams.set('ui_locales', locale.canonical);
                uplUrl.searchParams.set('acr_values', 'urn:eg:acr:4:authenticated:full');
                uplUrl.searchParams.set('redirectUri', loginInterstitialLink);
                return uplUrl.toString();
            }
            // This should never be called server side, but we have to return something.
            return '';
        }
    },
    {
        name: pages.JOIN_CREATION,
        link: 'join-creation',
        nextPages: [
            {
                predicate: [
                    predicates.isTimeoutJel
                ],
                pageName: pages.JOIN_EMAIL
            },
            {
                pageName: pages.CL_CONTRACT_ONBOARDING
            }
        ]
    },
    {
        name: pages.AGENTIC_JOIN_CREATION,
        link: 'agentic/join-creation',
        nextPages: [
            {
                predicate: [
                    predicates.isTimeoutJel
                ],
                pageName: pages.JOIN_EMAIL
            },
            {
                pageName: pages.CL_CONTRACT_ONBOARDING_AGENTIC
            }
        ]
    },
    {
        name: pages.AAP_JOIN_CREATION,
        link: 'add-property/join-creation',
        nextPages: [
            {
                predicate: [
                    predicates.isEg,
                    predicates.isClRejected
                ],
                pageName: pages.CL_ELIGIBILITY_CHECK
            },
            {
                predicate: [
                    predicates.isEg
                ],
                pageName: pages.CL_CONTRACT_ONBOARDING_AAP
            }
        ]
    },
    {
        name: pages.JOIN_EMAIL,
        link: 'join-creation/confirm'
    }
];

export {
    pageStates
};
